import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";

const summarizeDrops = (dropReasons) => {
  const entries = Object.entries(dropReasons || {});
  if (!entries.length) return "";
  return entries.sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ");
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, RefreshCw, Gauge, Target, DollarSign, Timer, AlertTriangle, ShieldCheck, Sparkles } from "lucide-react";

const usd = (v) => (v == null ? "—" : `$${Number(v).toFixed(v < 1 ? 3 : 2)}`);
const ms = (v) => (v == null ? "—" : v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`);
const pctText = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const isAdminPath = () => window.location.pathname.startsWith("/admin");

const Stat = ({ icon: Icon, label, value, hint, testid, tone = "text-white" }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500">
      <Icon className="h-3.5 w-3.5" />{label}
    </div>
    <p className={`mt-2 text-2xl font-semibold ${tone}`} data-testid={`${testid}-value`}>{value}</p>
    {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
  </div>
);

const QuotaBar = ({ id, label, used, limit, format = (v) => v }) => {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = limit && used > limit;
  const tone = over ? "bg-red-500" : pct >= 80 ? "bg-amber-400" : "bg-ism-teal";
  return (
    <div data-testid={`quota-bar-${id}`}>
      <div className="flex justify-between text-xs text-gray-400">
        <span>{label}</span>
        <span className={over ? "text-red-300" : "text-gray-300"} data-testid={`quota-bar-${id}-text`}>{format(used)} / {format(limit)}</span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full ${tone} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Funnel = ({ funnel }) => {
  const steps = [["raw", "Web results screened"], ["companies", "Registry firms found"], ["deduped", "Unique candidates"], ["gated", "Passed quality gate"]];
  const max = Math.max(1, ...steps.map(([k]) => funnel[k] || 0));
  return (
    <div className="space-y-2" data-testid="metrics-funnel">
      {steps.map(([k, label]) => (
        <div key={k} className="flex items-center gap-3 text-xs">
          <span className="w-40 text-gray-400">{label}</span>
          <div className="flex-1 h-2 rounded bg-white/5 overflow-hidden">
            <div className="h-full bg-ism-teal/70" style={{ width: `${Math.max(2, ((funnel[k] || 0) / max) * 100)}%` }} />
          </div>
          <span className="w-12 text-right text-gray-200" data-testid={`funnel-${k}`}>{funnel[k] || 0}</span>
        </div>
      ))}
    </div>
  );
};

const GradeStrip = ({ grades }) => (
  <div className="grid grid-cols-3 gap-2" data-testid="metrics-grades">
    {["A", "B", "C"].map((g) => {
      const d = grades[g] || { surfaced: 0, saved: 0, dismissed: 0 };
      const tone = g === "A" ? "text-emerald-300 border-emerald-500/30" : g === "B" ? "text-sky-300 border-sky-500/30" : "text-gray-300 border-white/10";
      return (
        <div key={g} className={`rounded-lg border bg-white/[0.02] p-3 ${tone}`} data-testid={`grade-${g}`}>
          <p className="text-xs uppercase tracking-wider opacity-70">Grade {g}</p>
          <p className="text-xl font-semibold">{d.surfaced}</p>
          <p className="text-[11px] text-gray-500">{d.saved} saved · {d.dismissed} dismissed</p>
        </div>
      );
    })}
  </div>
);

const SourceTable = ({ sources }) => (
  <div className="overflow-x-auto" data-testid="metrics-source-table">
    <table className="w-full text-xs">
      <thead className="text-gray-500 uppercase tracking-wider text-[10px]">
        <tr className="text-left border-b border-white/10">
          <th className="py-2 pr-3">Source</th><th className="py-2 pr-3 text-right">Surfaced</th><th className="py-2 pr-3 text-right">Saved</th>
          <th className="py-2 pr-3 text-right">Dismissed</th><th className="py-2 pr-3 text-right">Precision</th><th className="py-2 text-right">Ranking effect</th>
        </tr>
      </thead>
      <tbody>
        {sources.length === 0 && (
          <tr><td colSpan={6} className="py-4 text-gray-500">No prospects surfaced in this window yet — run a Hunt.</td></tr>
        )}
        {sources.map((s) => (
          <tr key={s.sourceId} className="border-b border-white/5 text-gray-300" data-testid={`source-row-${s.sourceId}`}>
            <td className="py-2 pr-3"><span className="text-gray-100">{s.name}</span><span className="ml-2 text-[10px] text-gray-500">{s.kind}</span></td>
            <td className="py-2 pr-3 text-right">{s.surfaced}</td>
            <td className="py-2 pr-3 text-right text-emerald-300">{s.saved}</td>
            <td className="py-2 pr-3 text-right text-gray-400">{s.dismissed}</td>
            <td className="py-2 pr-3 text-right" data-testid={`source-precision-${s.sourceId}`}>
              {s.precision == null ? <span className="text-gray-600">{s.samplesNeeded} more to learn</span> : pctText(s.precision)}
            </td>
            <td className={`py-2 text-right ${s.weightAdj > 0 ? "text-emerald-300" : s.weightAdj < 0 ? "text-amber-300" : "text-gray-500"}`}>
              {s.weightAdj > 0 ? `+${s.weightAdj} pts` : s.weightAdj < 0 ? `${s.weightAdj} pts` : "neutral"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const UsageBreakdown = ({ usage, unitLabels, unitCosts }) => {
  const keys = Object.keys(usage.units || {});
  return (
    <div data-testid="metrics-usage">
      <p className="text-xs text-gray-400 mb-2">{usage.month} · {usage.hunts} hunts · {usage.streamRuns} stream runs · {usage.prospects} prospects · est. {usd(usage.costUSD)}</p>
      {keys.length === 0 ? <p className="text-xs text-gray-600">No metered usage this month yet.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {keys.map((k) => (
            <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2" data-testid={`usage-unit-${k}`}>
              <p className="text-[11px] text-gray-500">{unitLabels[k] || k}</p>
              <p className="text-sm text-gray-200">{usage.units[k]} <span className="text-gray-500">× {usd(unitCosts[k] || 0)}</span></p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RecentRuns = ({ runs }) => (
  <div className="space-y-1.5" data-testid="metrics-recent-runs">
    {runs.length === 0 && <p className="text-xs text-gray-600">No runs yet.</p>}
    {runs.map((r, i) => (
      <div key={r.runId || `${r.timestamp}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400 border-b border-white/5 py-1.5">
        <span className={`px-1.5 py-0.5 rounded uppercase tracking-wider text-[10px] ${r.mode === "stream" ? "bg-sky-500/15 text-sky-300" : "bg-ism-teal/15 text-ism-teal"}`}>{r.mode}</span>
        <span>{new Date(r.timestamp).toLocaleString()}</span>
        <span className="text-gray-300">{r.gated ?? 0} prospects</span>
        <span>{r.companies ?? 0} firms · {r.anchored ?? 0} anchored</span>
        <span>{ms(r.durationMs)}</span>
        <span>{usd(r.costUSD)}</span>
        <span className="text-gray-600">{r.searchProvider}</span>
      </div>
    ))}
  </div>
);

const QuotaEditor = ({ userId, quota, onSaved }) => {
  const [form, setForm] = useState({ monthlyHunts: quota.hunts, monthlyProspects: quota.prospects, monthlyCostUSD: quota.costUSD });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/prospecting/quota/${userId}`, { monthlyHunts: Number(form.monthlyHunts), monthlyProspects: Number(form.monthlyProspects), monthlyCostUSD: Number(form.monthlyCostUSD) });
      toast.success("Quota updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update quota"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="quota-editor">
      {[["monthlyHunts", "Hunts / month"], ["monthlyProspects", "Prospects / month"], ["monthlyCostUSD", "Spend cap (USD)"]].map(([k, label]) => (
        <label key={k} className="text-[11px] text-gray-500">{label}
          <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-8 w-32 text-xs bg-black/30 border-white/10" data-testid={`quota-input-${k}`} />
        </label>
      ))}
      <Button size="sm" onClick={save} disabled={busy} className="h-8 bg-ism-teal hover:bg-ism-teal/90 text-white" data-testid="quota-save-btn">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save quota"}
      </Button>
    </div>
  );
};

export const ProspectingMetrics = ({ userId }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/prospecting/metrics/${userId}?days=${days}`);
      setData(r.data);
    } catch { toast.error("Could not load prospecting metrics"); } finally { setLoading(false); }
  }, [userId, days]);

  useEffect(() => { load(); }, [load]);
  const dropSummary = useMemo(() => summarizeDrops(data?.runs?.dropReasons), [data]);

  if (loading && !data) return <div className="flex items-center gap-2 text-gray-400 text-sm p-6" data-testid="metrics-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading quality &amp; usage…</div>;
  if (!data) return null;
  const { runs, prospects, grades, sources, usage, quota, recentRuns, unitLabels, unitCosts } = data;

  return (
    <div className="space-y-6" data-testid="prospecting-metrics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2"><Gauge className="h-4 w-4 text-ism-teal" />Quality &amp; Usage</h3>
          <p className="text-xs text-gray-500 mt-0.5">How precise each data source is for you, what prospecting costs, and where you stand against your monthly quota.</p>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${days === d ? "border-ism-teal text-ism-teal bg-ism-teal/10" : "border-white/10 text-gray-400 hover:text-white"}`} data-testid={`metrics-window-${d}`}>{d}d</button>
          ))}
          <Button size="sm" variant="outline" onClick={load} className="h-7 border-white/10 ml-1" data-testid="metrics-refresh-btn"><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      {quota.message && (
        <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${quota.exceeded ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`} data-testid="quota-banner">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{quota.message}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Target} label="Precision" value={pctText(prospects.precision)} hint={`${prospects.saved} saved · ${prospects.dismissed} dismissed`} testid="stat-precision" tone="text-emerald-300" />
        <Stat icon={Sparkles} label="Prospects surfaced" value={prospects.surfaced} hint={`${prospects.named} named · ${prospects.firmOnly} firm-only`} testid="stat-surfaced" />
        <Stat icon={DollarSign} label="Cost per saved lead" value={usd(prospects.costPerSavedLeadUSD)} hint={`${usd(runs.costUSD)} est. across ${runs.count} runs`} testid="stat-cost" />
        <Stat icon={Timer} label="Avg run time" value={ms(runs.avgDurationMs)} hint={`${runs.providerErrors} provider errors`} testid="stat-latency" />
      </div>

      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-200 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-ism-teal" />Monthly quota · {quota.plan} plan · soft limit</h4>
          <span className="text-[11px] text-gray-500">{quota.month}</span>
        </div>
        <div className="mt-3 grid md:grid-cols-3 gap-4">
          <QuotaBar id="hunts" label="Hunts & stream runs" used={quota.used.hunts} limit={quota.quota.hunts} />
          <QuotaBar id="prospects" label="Prospects surfaced" used={quota.used.prospects} limit={quota.quota.prospects} />
          <QuotaBar id="cost" label="Estimated provider spend" used={quota.used.costUSD} limit={quota.quota.costUSD} format={usd} />
        </div>
        <p className="mt-2 text-[11px] text-gray-500">When a limit is passed, hunting continues and the overage is recorded in the usage meter.</p>
        {isAdminPath() && <QuotaEditor userId={userId} quota={quota.quota} onSaved={load} />}
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h4 className="text-sm font-medium text-gray-200 mb-3">Pipeline funnel ({days}d)</h4>
          <Funnel funnel={runs.funnel} />
          {dropSummary && (
            <p className="mt-3 text-[11px] text-gray-500" data-testid="metrics-drop-reasons">
              Rejected: {dropSummary}
            </p>
          )}
        </section>
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h4 className="text-sm font-medium text-gray-200 mb-3">Confidence grades</h4>
          <GradeStrip grades={grades} />
          <p className="mt-3 text-[11px] text-gray-500">A = verified firm + verified person · B = partially verified · C = low confidence.</p>
        </section>
      </div>

      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h4 className="text-sm font-medium text-gray-200 mb-1">Source precision &amp; learned weights</h4>
        <p className="text-[11px] text-gray-500 mb-3">Saving or dismissing a hunt prospect teaches the planner. After 3 decisions per source, its precision nudges ranking by up to ±8 points.</p>
        <SourceTable sources={sources} />
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h4 className="text-sm font-medium text-gray-200 mb-3">Metered usage (billing export)</h4>
          <UsageBreakdown usage={usage} unitLabels={unitLabels} unitCosts={unitCosts} />
        </section>
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h4 className="text-sm font-medium text-gray-200 mb-3">Recent runs</h4>
          <RecentRuns runs={recentRuns} />
        </section>
      </div>
    </div>
  );
};
